import React, { useState, lazy, Suspense, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Header/Header';
import SelfAssessmentHeader from './components/SelfAssessmentHeader/SelfAssessmentHeader';
import Protected from './components/Protected/Protected';
import ErrorBoundary from './components/ErrorBoundary';
import SkipLink from './components/SkipLink';
import Login from './screens/Login/Login';
import { ToastProvider, ToastContainer } from './components/Toast';
import { AuthProvider, useAuth } from './context/AuthContext';

import './App.scss';

// Lazy-loaded route components for code splitting
const Dashboard = lazy(() => import('./screens/Dashboard/Dashboard'));
const Patients = lazy(() => import('./screens/Patients/Patients'));
const NotFound = lazy(() => import('./screens/NotFound/NotFound'));
const Analysis = lazy(() => import('./screens/Analysis/Analysis'));
const PreTreatment = lazy(() => import('./screens/SelfAssessment/PreTreatment/PreTreatment'));
const AfterXWeeks = lazy(() => import('./screens/SelfAssessment/AfterXWeeks/AfterXWeeks'));
const AfterXMonths = lazy(() => import('./screens/SelfAssessment/AfterXMonths/AfterXMonths'));
const AfterXYears = lazy(() => import('./screens/SelfAssessment/AfterXYears/AfterXYears'));
const PatientDashboard = lazy(() => import('./screens/PatientDashboard/PatientDashboard'));
const EnrollPatient = lazy(() => import('./screens/EnrollPatient/EnrollPatient'));
const AddSurgery = lazy(() => import('./screens/AddSurgery/AddSurgery'));
const ClinicVisit = lazy(() => import('./screens/ClinicVisit/ClinicVisit'));
const SurgeryDetails = lazy(() => import('./screens/SurgeryDetails/SurgeryDetails'));
const EditPreTreatmentSurvey = lazy(() => import('./screens/EditSurvey/EditPreTreatmentSurvey'));
const Notifications = lazy(() => import('./screens/Notifications/Notifications'));
const PatientCompliance = lazy(() => import('./screens/PatientCompliance/PatientCompliance'));
const Complication = lazy(() => import('./screens/Complication/Complication'));
const PatientComplications = lazy(() => import('./screens/PatientComplications/PatientComplications'));
const SurgeonTasks = lazy(() => import('./screens/SurgeonTasks/SurgeonTasks'));
const PriorityList = lazy(() => import('./screens/PriorityList/PriorityList'));
const SurgeonSettings = lazy(() => import('./screens/SurgeonSettings/SurgeonSettings'));
const Unsubscribe = lazy(() => import('./screens/Unsubscribe/Unsubscribe'));
const SetPassword = lazy(() => import('./screens/SetPassword/SetPassword'));
const AccessDenied = lazy(() => import('./screens/AccessDenied/AccessDenied'));

// Loading fallback for lazy-loaded components
const LoadingFallback = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

// Role-based route protection component
const RoleProtectedRoute = ({ children, allowedRoles }) => {
  const { isLoggedIn, hasRole } = useAuth();

  if (!isLoggedIn) {
    return <Login />;
  }

  // If allowedRoles is specified and user doesn't have required role
  if (allowedRoles && !hasRole(allowedRoles)) {
    return <AccessDenied />;
  }

  return children;
};

const AppContent = () => {
  const { pathname } = useLocation();
  const { isLoggedIn, user, logout } = useAuth();

  const [patientDateRange, setPatientDateRange] = useState(() => {
    const date = new Date();
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return [firstDay, lastDay];
  });
  const [numberOfNotifications, setNumberOfNotifications] = useState(0);

  // Global effect to add 'has-value' class to select elements with values
  useEffect(() => {
    const updateSelectHasValue = (select) => {
      if (select.value && select.value !== '') {
        select.classList.add('has-value');
      } else {
        select.classList.remove('has-value');
      }
    };

    // Handle change events on selects (using event delegation)
    const handleSelectChange = (e) => {
      if (e.target.tagName === 'SELECT') {
        updateSelectHasValue(e.target);
      }
    };

    // Initial pass: mark all selects that already have values
    const initializeSelects = () => {
      document.querySelectorAll('select').forEach(updateSelectHasValue);
    };

    // Run initial pass
    initializeSelects();

    // Listen for changes using event delegation
    document.addEventListener('change', handleSelectChange);

    // Re-initialize selects when route changes (handles dynamically added selects)
    const reinitializeTimeout = setTimeout(initializeSelects, 100);

    return () => {
      document.removeEventListener('change', handleSelectChange);
      clearTimeout(reinitializeTimeout);
    };
  }, [pathname]);

  const showSelfAssessmentHeader = pathname.includes('self-assessment');
  const hideHeader = pathname.includes('unsubscribe') || pathname.includes('set-password') || pathname.includes('access-denied') || pathname === '/' || pathname === '/login';
  const isPublicPage = showSelfAssessmentHeader || hideHeader;

  return (
    <div className="App">
      <SkipLink targetId="main-content">Skip to main content</SkipLink>
      <ToastContainer/>
      <ErrorBoundary>
        {showSelfAssessmentHeader ? (
          <header className="header" role="banner">
            <SelfAssessmentHeader />
          </header>
        ) : !hideHeader ? (
          <header className="header" role="banner">
            <Header numberOfNotifications={numberOfNotifications} setNumberOfNotifications={setNumberOfNotifications}/>
          </header>
        ) : null}

        <main id="main-content" role="main" className={`otos-container ${isPublicPage ? '' : 'container-xxl'}`}>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<Login />} />
              <Route path="/login" element={<Login />} />
              <Route path="/dashboard" element={<Protected loggedIn={isLoggedIn}><Dashboard user={user} logUserOut={logout}/></Protected>} />

              <Route path="/patients" element={<Protected loggedIn={isLoggedIn}><Patients user={user} logUserOut={logout} dateRange={patientDateRange} setDateRange={setPatientDateRange}/></Protected>} />
              <Route path="/patients/new" element={<RoleProtectedRoute allowedRoles={['admin', 'surgeon', 'data_nurse']}><EnrollPatient newPatient={true} user={user} logUserOut={logout}/></RoleProtectedRoute>} />
              <Route path="/patients/:customPatientId" element={<Protected loggedIn={isLoggedIn}><PatientDashboard user={user} logUserOut={logout}/></Protected>} />
              <Route path="/patients/:customPatientId/edit" element={<RoleProtectedRoute allowedRoles={['admin', 'surgeon', 'data_nurse']}><EnrollPatient newPatient={false} user={user} logUserOut={logout}/></RoleProtectedRoute>} />

              <Route path="/patients/:customPatientId/events/new/surgery" element={<RoleProtectedRoute allowedRoles={['admin', 'surgeon', 'data_nurse']}><AddSurgery newSurgery={true} user={user} logUserOut={logout}/></RoleProtectedRoute>} />
              <Route path="/patients/:customPatientId/events/new/clinic-visit" element={<RoleProtectedRoute allowedRoles={['admin', 'surgeon']}><ClinicVisit newClinicVisit={true} user={user} logUserOut={logout}/></RoleProtectedRoute>} />
              <Route path="/patients/:customPatientId/events/new/complication" element={<RoleProtectedRoute allowedRoles={['admin', 'surgeon']}><Complication newComplication={true} user={user} logUserOut={logout}/></RoleProtectedRoute>} />
              <Route path="/patients/:customPatientId/events/surgery/:eventId/edit" element={<RoleProtectedRoute allowedRoles={['admin', 'surgeon', 'data_nurse']}><AddSurgery newSurgery={false} user={user} logUserOut={logout}/></RoleProtectedRoute>} />
              <Route path="/patients/:customPatientId/events/surgery/:eventId" element={<RoleProtectedRoute allowedRoles={['admin', 'surgeon', 'data_nurse']}><SurgeryDetails user={user} logUserOut={logout}/></RoleProtectedRoute>} />
              <Route path="/patients/:customPatientId/events/clinic-visit/:eventId/edit" element={<RoleProtectedRoute allowedRoles={['admin', 'surgeon']}><ClinicVisit newClinicVisit={false} user={user} logUserOut={logout}/></RoleProtectedRoute>} />
              <Route path="/patients/:customPatientId/events/complication/:eventId/edit" element={<RoleProtectedRoute allowedRoles={['admin', 'surgeon']}><Complication newComplication={false} user={user} logUserOut={logout}/></RoleProtectedRoute>} />

              <Route path="/notifications" element={<Protected loggedIn={isLoggedIn}><Notifications user={user} logUserOut={logout} setNumberOfNotifications={setNumberOfNotifications}/></Protected>} />
              <Route path="/analysis" element={<RoleProtectedRoute allowedRoles={['admin', 'surgeon']}><Analysis user={user} logUserOut={logout}/></RoleProtectedRoute>} />

              <Route path="/surgeon-tasks" element={<RoleProtectedRoute allowedRoles={['admin', 'surgeon']}><SurgeonTasks user={user} logUserOut={logout}/></RoleProtectedRoute>} />
              <Route path="/surgeon-settings" element={<RoleProtectedRoute allowedRoles={['admin', 'surgeon']}><SurgeonSettings user={user} logUserOut={logout}/></RoleProtectedRoute>} />

              <Route path="/patient-compliance" element={<RoleProtectedRoute allowedRoles={['admin', 'surgeon']}><PatientCompliance user={user} logUserOut={logout}/></RoleProtectedRoute>} />
              <Route path="/patient-complications" element={<RoleProtectedRoute allowedRoles={['admin', 'surgeon']}><PatientComplications user={user} logUserOut={logout}/></RoleProtectedRoute>} />
              <Route path="/priority-list" element={<RoleProtectedRoute allowedRoles={['admin', 'surgeon', 'data_nurse']}><PriorityList user={user} logUserOut={logout}/></RoleProtectedRoute>} />

              <Route path="/patient/:customPatientId/event/:eventId/:study/:treatmentSide/self-assessment/pre-treatment" element={<PreTreatment />} />
              <Route path="/patient/:customPatientId/event/:eventId/:study/:treatmentSide/self-assessment/week/:week" element={<AfterXWeeks />} />
              <Route path="/patient/:customPatientId/event/:eventId/:study/:treatmentSide/self-assessment/month/:month" element={<AfterXMonths />} />
              <Route path="/patient/:customPatientId/event/:eventId/:study/:treatmentSide/self-assessment/year/:year" element={<AfterXYears />} />

              <Route path="/patient/:customPatientId/event/:eventId/:study/:treatmentSide/survey/:stage/new" element={<EditPreTreatmentSurvey createNewSurvey={true} user={user} logUserOut={logout}/>} />
              <Route path="/patient/:customPatientId/event/:eventId/:study/:treatmentSide/survey/:stage/edit" element={<EditPreTreatmentSurvey createNewSurvey={false} user={user} logUserOut={logout}/>} />

              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/set-password" element={<SetPassword />} />
              <Route path="/access-denied" element={<AccessDenied />} />
              <Route path="/404" element={<NotFound />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
      </ErrorBoundary>
    </div>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <ToastProvider>
        <AppContent />
      </ToastProvider>
    </AuthProvider>
  );
};

export default App;
